import { FC, memo } from 'react';
import type { ReactNode } from 'react';
import type { Dayjs } from 'dayjs';
import { Select } from 'antd';
import { RANGE_PRESENT } from './constants';
import { Clinic } from '../../../../models/MedVox/Clinic';
import { RangeValue } from '../../../../hooks/components/useOutgoingStatistics/types';
import { OptionLike } from '../../../../hooks/shared/useFilterOption';
import styles from './style.module.css';
import { ClinicSelect } from '../../../../components/shared/ClinicSelect';
import { FILTER_FIELD_TEXTS } from '../../../../components/shared/filterFieldTexts';
import { BatchForm, FormTemplate } from '../../../../models/TaskForm/TaskForm';
import { useFilterOptions } from '../../../../hooks/shared/useFilterOptions';
import { ResettableRangePicker } from '../../../../components/shared/ResettableRangePicker/ResettableRangePicker';
import type { ResettableRangeValue } from '../../../../components/shared/ResettableRangePicker/ResettableRangePicker';

interface Props {
  onClinicsChange: (e: string[]) => void;
  clinics: Clinic[];
  onRangePickerChange: (
    values: RangeValue<Dayjs>,
    formatString: string[],
  ) => void;
  dateRange: ResettableRangeValue;
  filterOption: (
    inputValue: string,
    option?: OptionLike | undefined,
  ) => boolean;
  clinicValue: string[];
  onOpenClinics: (value: boolean) => void;
  scenarioValue: string | null;
  scenarioOptions: Array<{ value: string; label: string }>;
  onScenarioChange: (value: string | undefined) => void;
  campaignFilter?: ReactNode;
  showCampaignFilter?: boolean;
  campaigns?: BatchForm[];
  campaignValue?: string[];
  onCampaignsChange?: (value: string[]) => void;
  onOpenCampaigns?: (value: boolean) => void;
  showTemplateFilter: boolean;
  templates: FormTemplate[];
  templateValue: string[];
  onTemplatesChange: (value: string[]) => void;
  onOpenTemplates: (value: boolean) => void;
}

export const OutgoingStatisticsFilter: FC<Props> = memo(
  ({
    onClinicsChange,
    clinics,
    onRangePickerChange,
    dateRange,
    clinicValue,
    filterOption,
    onOpenClinics,
    scenarioValue,
    scenarioOptions,
    onScenarioChange,
    campaignFilter,
    showCampaignFilter,
    campaigns = [],
    campaignValue = [],
    onCampaignsChange,
    onOpenCampaigns,
    showTemplateFilter,
    templates,
    templateValue,
    onTemplatesChange,
    onOpenTemplates,
  }: Props) => {
    const renderOption = (state: { label?: React.ReactNode }) => (
      <span className={styles.inputSpan}> {state.label}</span>
    );

    const campaignOptions = useFilterOptions(
      campaigns,
      campaignValue,
      (campaign) => campaignValue.includes(campaign.id),
      (campaign) => ({ value: campaign.id, label: campaign.name }),
    );

    const templateOptions = useFilterOptions(
      templates,
      templateValue,
      (template) => templateValue.includes(template.id),
      (template) => ({ value: template.id, label: template.name }),
    );

    return (
      <>
        <div className={styles.filterBlock}>
          <div className={styles.inputsContainer}>
            <label>Период звонков</label>
            <ResettableRangePicker
              className={styles.dateInput}
              presets={RANGE_PRESENT}
              value={dateRange}
              onChange={onRangePickerChange}
            />
          </div>
          <div className={styles.inputsContainer}>
            <label>{FILTER_FIELD_TEXTS.notificationType.label}</label>
            <Select
              allowClear
              className={styles.input}
              placeholder={FILTER_FIELD_TEXTS.notificationType.placeholder}
              value={scenarioValue ?? undefined}
              filterOption={filterOption}
              onChange={onScenarioChange}
              showSearch
              options={scenarioOptions}
              autoClearSearchValue={false}
              optionRender={renderOption}
            />
          </div>
          <div className={styles.inputsContainer}>
            <label>{FILTER_FIELD_TEXTS.clinic.label}</label>
            <ClinicSelect
              clinics={clinics}
              value={clinicValue}
              onChange={onClinicsChange}
              className={styles.input}
              placeholder={FILTER_FIELD_TEXTS.clinic.placeholder}
              filterOption={filterOption}
              onOpenChange={onOpenClinics}
              optionRender={renderOption}
            />
          </div>
          {campaignFilter ??
            (showCampaignFilter ? (
              <div className={styles.inputsContainer}>
                <label>{FILTER_FIELD_TEXTS.batch.label}</label>
                <Select
                  allowClear
                  className={styles.input}
                  placeholder={FILTER_FIELD_TEXTS.batch.placeholder}
                  mode="multiple"
                  value={campaignValue}
                  filterOption={filterOption}
                  onChange={onCampaignsChange}
                  showSearch
                  onOpenChange={onOpenCampaigns}
                  removeIcon={null}
                  maxTagCount="responsive"
                  maxTagTextLength={24}
                  options={campaignOptions}
                  autoClearSearchValue={false}
                  optionRender={renderOption}
                />
              </div>
            ) : null)}
          {showTemplateFilter ? (
            <div className={styles.inputsContainer}>
              <label>{FILTER_FIELD_TEXTS.surveyTemplate.label}</label>
              <Select
                allowClear
                className={styles.input}
                placeholder={FILTER_FIELD_TEXTS.surveyTemplate.placeholder}
                mode="multiple"
                value={templateValue}
                filterOption={filterOption}
                onChange={onTemplatesChange}
                showSearch
                onOpenChange={onOpenTemplates}
                removeIcon={null}
                maxTagCount="responsive"
                maxTagTextLength={24}
                options={templateOptions}
                autoClearSearchValue={false}
                optionRender={renderOption}
              />
            </div>
          ) : null}
        </div>
      </>
    );
  },
);

OutgoingStatisticsFilter.displayName = 'OutgoingStatisticsFilter';
